'use client';

import React, { useContext, useMemo, useState } from 'react';

import APP_CONFIG from '@whistle/config';

import { MessagingContextValues, MessagingProviderProps } from './types';
import { useChannel } from '../websocket/useChannel';
import { useEvent } from '../websocket/useEvent';

import { Message, SendMessageData } from '@/models';
import { MessagesService } from '@/services/messages';
import { ClientCookiesStore } from '@/services/cookies/client';
import { transformMessage } from '@/services/messages/transform';

export const MessagingContext = React.createContext<MessagingContextValues>({
  sendMessage: async () => {},
  fetchPreviousMessages: async () => {},
  fetchMessages: async () => {},
  markAsRead: async () => {},
  messages: [],
  unread: false,
  unreadMessageCount: 0,
});

export const MessagingProvider: React.FC<MessagingProviderProps> = ({
  company,
  guestId,
  messageData: defaultMessagesData,
  children,
}) => {
  const [lastEvaluatedDatetime, setLastEvaluatedDatetime] = useState<
    string | undefined
  >(defaultMessagesData.lastEvaluatedDatetime);
  const [messages, setMessages] = useState<Message[]>(
    defaultMessagesData.messages,
  );
  const guestChannel = useChannel(
    `private-guest-${APP_CONFIG.ENV}-${company}-mfd-${guestId}`,
  );

  useEvent(guestChannel, 'message:created', (message) => {
    setMessages((prevMessages) => [
      transformMessage({ ...message, from_name: message.fromName }),
      ...prevMessages,
    ]);
  });

  const sendMessage = async (message: SendMessageData) => {
    const uid = `new_` + Math.random().toString(36).substring(7);
    setMessages((prevMessages) => [
      {
        ...message,
        datetime: new Date().toISOString(),
        inbound: true,
        queue_id: '',
        id: uid,
        parsedMedia: [],
        status_message: null,
      },
      ...prevMessages,
    ]);

    return new MessagesService(ClientCookiesStore)
      .sendMessage(company, message)
      .then((res) => {})
      .catch((error) => {
        setMessages((prevMessages) =>
          prevMessages.map((msg) => {
            if (msg.id === uid) {
              return {
                ...msg,
                status: 'error',
              };
            }
            return msg;
          }),
        );
      });
  };

  const fetchPreviousMessages = async () => {
    const messagesData = await new MessagesService(
      ClientCookiesStore,
    ).listMessages(company, lastEvaluatedDatetime);

    if (messagesData.messages.length === 0) {
      return Promise.resolve();
    }

    setMessages((prevMessages) => [...prevMessages, ...messagesData.messages]);
    setLastEvaluatedDatetime(messagesData.lastEvaluatedDatetime);

    return Promise.resolve();
  };

  const fetchMessages = async () => {
    const messagesData = await new MessagesService(
      ClientCookiesStore,
    ).listMessages(company);

    setMessages(messagesData.messages);
    setLastEvaluatedDatetime(messagesData.lastEvaluatedDatetime);
  };

  const markAsRead = async () => {
    const response = await new MessagesService(
      ClientCookiesStore,
    ).markAllAsRead(company);
  };

  const unread = useMemo(() => {
    return (
      messages.length > 0 &&
      !messages[0].inbound &&
      messages[0].status_message !== 'Read'
    );
  }, [messages]);

  const unreadMessageCount = useMemo(() => {
    return messages.filter(
      (message) => !message.inbound && message.status_message !== 'Read',
    ).length;
  }, [messages]);

  return (
    <MessagingContext.Provider
      value={{
        lastEvaluatedDatetime,
        messages,
        sendMessage,
        fetchPreviousMessages,
        fetchMessages,
        markAsRead,
        unread,
        unreadMessageCount,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
};

export function useMessaging() {
  return useContext<MessagingContextValues>(MessagingContext);
}
